import Input from '@mui/material/Input';
import Box from '@mui/system/Box';
import React, { useEffect, useRef, useState } from 'react';

interface NumericFilterInputProps {
    filterValue: [number | null, number | null];
    setFilterValue: (value: [number | null, number | null]) => void;
}

const NumericFilterInput: React.FC<NumericFilterInputProps> = ({
    filterValue,
    setFilterValue,
}) => {
    const [localFilterValues, setLocalFilterValues] = useState<[number | null, number | null]>(filterValue);

    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // clear timer when filter values change externally and sync local values
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
        setLocalFilterValues(filterValue);
    }, [filterValue]);

    const delay = 500;
    useEffect(() => {
        if (localFilterValues[0] !== null && isNaN(localFilterValues[0])) {
            return;
        }
        if (localFilterValues[1] !== null && isNaN(localFilterValues[1])) {
            return;
        }

        const timeout = setTimeout(() => {
            // commit only if different from latest prop values
            if (localFilterValues[0] !== filterValue[0] || localFilterValues[1] !== filterValue[1]) {
                setFilterValue(localFilterValues);
            }
            timer.current = null;
        }, delay);

        // store current timeout id so it can be cleared externally
        timer.current = timeout;

        return () => {
            clearTimeout(timeout);
            if (timer.current === timeout) {
                timer.current = null;
            }
        };
    }, [localFilterValues, setFilterValue, filterValue]);

    const handleSetMin = (value: number | null) => {
        if (value === undefined || value === null || isNaN(value)) value = null;
        if (value === localFilterValues[0]) return;
        setLocalFilterValues([value, localFilterValues[1]]);
    }

    const handleSetMax = (value: number | null) => {
        if (value === undefined || value === null || isNaN(value)) value = null;
        if (value === localFilterValues[1]) return;
        setLocalFilterValues([localFilterValues[0], value]);
    }

    return (
       <Box flexDirection={'row'} display='flex'>
            <Input
                type="number"
                placeholder="Min Value"
                value={localFilterValues[0] ?? undefined}
                style={{ marginRight: '10px', marginTop: '10px' }}
                onChange={(e) => handleSetMin(parseFloat(e.target.value ?? ''))}
            />
            <Input
                type="number"
                placeholder="Max Value"
                value={localFilterValues[1] ?? undefined}
                style={{ marginTop: '10px' }}
                onChange={(e) => handleSetMax(parseFloat(e.target.value ?? ''))}
            />
        </Box>
    );
};

export default NumericFilterInput;